import { Code, ConnectError } from "@connectrpc/connect";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@viajero/shared-ui/analytics";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useGetProductDetail } from "@/features/courses/api/useGetProductDetail";
import { useCreateCheckoutSession } from "@/features/reservation/api/useCreateCheckoutSession";
import { AgreementCheckboxes } from "@/features/reservation/components/AgreementCheckboxes";
import { AllergyRequestSection } from "@/features/reservation/components/AllergyRequestSection";
import { BuyerInfoForm } from "@/features/reservation/components/BuyerInfoForm";
import { EmailVerificationButton } from "@/features/reservation/components/EmailVerificationButton";
import { PurchaseSummarySection } from "@/features/reservation/components/PurchaseSummarySection";
import { markCheckoutStarted } from "@/lib/bookingDraftStorage";
import { calculateTotalPrice } from "@/lib/calculateTotalPrice";
import { useCourseBookingContext } from "@/lib/courseBookingContext";
import { isValidId } from "@/lib/id";
import { isBookingSelectionValid } from "@/lib/isBookingSelectionValid";
import { saveLastShopLocation } from "@/lib/lastShopLocation";
import { toDateInputValue } from "@/lib/toDateInputValue";
import { colors } from "@/theme/colors";

type Props = {
  caseToken: string;
  shopId: string;
  productId: string;
  onBack: () => void;
  onBackToShop: () => void;
};

/**
 * 購入者情報入力画面。コース詳細画面 (CourseBookingProvider) で選択した利用日時・人数と
 * 商品情報を組み合わせてご購入内容を表示し、購入者情報の入力・メールアドレス認証を行う。
 *
 * メールアドレス認証 (EmailVerificationButton) は RequestEmailVerification / VerifyEmailCode を
 * 呼ぶ (§6.4。案件に紐付き予約 ID は不要)。「決済に進む」押下では CreateCheckoutSession に予約内容
 * (または再決済の予約 ID) を渡して予約を作成し、返却された Stripe Checkout の URL へリダイレクト
 * する (§6.5。JPTP_CANSEI-1678)。
 */
export function BuyerInfoStep({ caseToken, shopId, productId, onBack, onBackToShop }: Props) {
  const { t, i18n } = useTranslation();
  const query = useGetProductDetail(shopId, productId, Boolean(caseToken));
  const { selection, buyerInfo, setBuyerInfo, setSelection, reservationId, returnedFromCheckout } =
    useCourseBookingContext();
  const createCheckoutSession = useCreateCheckoutSession();
  // 言語切替に追従できるよう、state には翻訳済み文字列ではなく i18n キーを保持する。
  const [checkoutErrorKey, setCheckoutErrorKey] = useState<string | null>(null);
  // CreateCheckoutSession の冪等化キー。マウント中は固定し、再送しても Stripe 側で二重発行しない (§6.5)。
  const idempotencyKeyRef = useRef<string>("");
  if (!idempotencyKeyRef.current) {
    idempotencyKeyRef.current = crypto.randomUUID();
  }

  const isNotFound =
    !isValidId(shopId) ||
    !isValidId(productId) ||
    (query.error instanceof ConnectError && query.error.code === Code.NotFound);

  if (query.isLoading || isNotFound || query.isError || !query.data?.product) {
    return (
      <QueryStateFallback
        isLoading={query.isLoading}
        isNotFound={isNotFound}
        isError={query.isError || !query.data?.product}
        notFound={{
          title: t("courseDetail.notFound"),
          description: t("courseDetail.notFoundDescription"),
          actions: [{ label: t("errorPage.backToShop"), onClick: onBackToShop }],
        }}
        error={{
          title: t("courseDetail.error"),
          description: t("courseDetail.errorDescription"),
          actions: [
            { label: t("errorPage.reload"), onClick: () => void query.refetch() },
            { label: t("errorPage.backToShop"), onClick: onBackToShop, variant: "outlined" },
          ],
        }}
      />
    );
  }

  const { product } = query.data;
  const totalPrice = calculateTotalPrice(product.prices, selection.quantities);
  const isFormValid =
    Boolean(buyerInfo.lastName) &&
    Boolean(buyerInfo.firstName) &&
    Boolean(buyerInfo.phoneNumber) &&
    Boolean(buyerInfo.email);
  // 利用日カレンダーの min と同じく本日を最小選択日として過去日予約を弾く。
  const minDate = toDateInputValue(new Date());
  const bookingValid = isBookingSelectionValid(selection, product, minDate);
  const agreementsValid =
    selection.agreedTerms && selection.agreedPrivacy && selection.agreedCancellation;
  // CreateCheckoutSession に渡す枚数入力 (price type ごと。0 枚は除外)。単価は Backend が再計算する (§6.5)。
  const tickets = product.prices
    .map((price) => ({
      priceType: price.priceType,
      quantity: selection.quantities[price.priceTypeId.toString()] ?? 0,
    }))
    .filter((ticket) => ticket.quantity > 0);
  const canProceed = isFormValid && buyerInfo.emailVerified && bookingValid && agreementsValid;
  const processing = createCheckoutSession.isPending;

  // CreateCheckoutSession のエラーは backend が種別ごとに別コードで返す (JPTP_CANSEI-1657)。
  // Bot 操作の失敗・判定不能 (Unavailable)、内部エラー (Internal)、case token の期限切れ
  // (Interceptor の FailedPrecondition) はいずれも時間をおいての再試行しかないため汎用文言。
  const keyForCheckoutError = (err: unknown): string => {
    if (err instanceof ConnectError) {
      switch (err.code) {
        case Code.ResourceExhausted:
          return "buyerInfo.checkoutErrorNoVacancy";
        case Code.Unauthenticated:
          return "buyerInfo.checkoutErrorEmailNotVerified";
      }
    }
    return "buyerInfo.checkoutError";
  };

  // 「決済に進む」押下。予約内容と認証済み予約者情報で予約を作成して Checkout Session を発行し、
  // 返却された Stripe Checkout URL へリダイレクトする (§6.5)。決済せずに戻ってきた後の再決済では
  // 保持している予約 ID を渡し、Backend が同じ予約の旧セッションを expire してから再発行する
  // (二重決済防止。§7.2)。予約 ID 指定時は予約内容を送っても使われない。
  const handleCheckout = async () => {
    trackEvent("click_proceed_payment");
    setCheckoutErrorKey(null);
    try {
      const res = await createCheckoutSession.mutateAsync({
        reservationId: reservationId ?? undefined,
        shopId: BigInt(shopId),
        productId: BigInt(productId),
        visitDate: selection.date,
        visitTime: selection.time,
        tickets,
        languageCode: i18n.language,
        guest: {
          lastName: buyerInfo.lastName,
          firstName: buyerInfo.firstName,
          phone: buyerInfo.phoneNumber,
          email: buyerInfo.email,
          allergy: buyerInfo.allergy,
          request: buyerInfo.request,
        },
        idempotencyKey: idempotencyKeyRef.current,
      });
      // 決済完了画面 (success_url) は caseToken / shopId を持たないため、Stripe へ出る直前に
      // 戻り先を退避する。決済に進んだ場合だけ記録する (JPTP_CANSEI-1522)。
      saveLastShopLocation({ caseToken, shopId });
      // 決済せずに戻ってきたときに再認証を促す案内を出すための印 (JPTP_CANSEI-1428) と、
      // 再決済で同じ予約を指定するための予約 ID を退避する (JPTP_CANSEI-1678)。
      markCheckoutStarted({ caseToken, shopId, productId }, res.reservationId);
      window.location.href = res.checkoutUrl;
    } catch (err) {
      // 認証切れのままだと EmailVerificationButton が「認証済み」表示で再認証できないため、
      // 未認証へ戻して認証ボタンを出し直す。await 中の入力を巻き戻さないよう更新関数で渡す。
      if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
        setBuyerInfo((info) => ({ ...info, emailVerified: false }));
      }
      setCheckoutErrorKey(keyForCheckoutError(err));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 640, width: "100%", mx: "auto" }}>
      {/* Stripe から決済せずに戻ってきた場合の案内 (JPTP_CANSEI-1428)。入力値は復元するが
          メールアドレス認証は backend 側で消し込まれているためやり直しが必要になる (§6.4)。
          再認証が済めば案内の役目は終わるため表示を取り下げる。 */}
      {returnedFromCheckout && !buyerInfo.emailVerified && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("buyerInfo.checkoutCanceledNotice")}
        </Alert>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, mb: 2 }}>
        {t("buyerInfo.sectionTitle")}
      </Typography>

      <BuyerInfoForm buyerInfo={buyerInfo} onChange={setBuyerInfo} />

      <Box sx={{ mt: 2 }}>
        <EmailVerificationButton
          languageCode={i18n.language}
          email={buyerInfo.email}
          verified={buyerInfo.emailVerified}
          bookingValid={bookingValid}
          onVerified={() => setBuyerInfo({ ...buyerInfo, emailVerified: true })}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, mb: 2 }}>
        {t("buyerInfo.purchaseContentTitle")}
      </Typography>
      <PurchaseSummarySection
        product={product}
        date={selection.date}
        time={selection.time}
        quantities={selection.quantities}
        totalPrice={totalPrice}
      />

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <AllergyRequestSection
          allergy={buyerInfo.allergy}
          request={buyerInfo.request}
          onAllergyChange={(allergy) => setBuyerInfo({ ...buyerInfo, allergy })}
          onRequestChange={(request) => setBuyerInfo({ ...buyerInfo, request })}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <AgreementCheckboxes
          agreedTerms={selection.agreedTerms}
          agreedPrivacy={selection.agreedPrivacy}
          agreedCancellation={selection.agreedCancellation}
          onAgreedTermsChange={(agreedTerms) => setSelection({ ...selection, agreedTerms })}
          onAgreedPrivacyChange={(agreedPrivacy) => setSelection({ ...selection, agreedPrivacy })}
          onAgreedCancellationChange={(agreedCancellation) =>
            setSelection({ ...selection, agreedCancellation })
          }
        />
      </Box>

      {checkoutErrorKey && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(checkoutErrorKey)}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={() => {
            trackEvent("click_change_conditions");
            onBack();
          }}
          sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 48 }}
        >
          {t("buyerInfo.changeConditions")}
        </Button>
        <Button
          variant="contained"
          disabled={!canProceed || processing}
          onClick={() => void handleCheckout()}
          sx={{ width: { xs: "100%", sm: "auto" }, minHeight: 48 }}
        >
          {processing ? <CircularProgress size={20} /> : t("buyerInfo.proceedToPayment")}
        </Button>
      </Box>
    </Box>
  );
}
